import { Co2MaterialSettingType, ProfileLevelType, RebarEstimationStatus, UpdateScenarioRequest, UpsertCo2MaterialSettingRequest } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCo2MaterialAssignSettings, useUpsertCo2MaterialSettings } from "@api/co2MaterialAssignSettings";
import { useProfileSearch } from "@api/profiles";
import { useUpdateScenario } from "@api/scenarios";
import i18n from "@i18n/i18n";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Switch from "@mui/material/Switch";
import { observer } from "mobx-react";
import { ChangeEvent, useEffect, useState } from "react";
import RebarEstimationGrid from "./RebarEstimationGrid";

export interface RebarEstimationPageProps {
  mode: Co2MaterialSettingType;
  projectId?: string | null;
  scenarioId?: string;
  profileId?: string | null;
  originScreen: "Admin" | "Project" | "Scenario";
  rebarEnabled?: RebarEstimationStatus;
}

const RebarEstimationPage = observer((props: RebarEstimationPageProps) => {
  const theme = useTheme();

  const [isChecked, setIsChecked] = useState(false);
  const [activeMode, setActiveMode] = useState<Co2MaterialSettingType>(props.mode);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");

  const { data: profileData } = useProfileSearch([ProfileLevelType.Application], null);
  const [profileOptions, setProfileOptions] = useState<{ label: string; value: string }[]>([]);
  const appSettings = useAppSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingProfileId, setPendingProfileId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const updateScenario = useUpdateScenario();

  const co2MaterialAssignSettings = useCo2MaterialAssignSettings(activeMode, props.projectId, props.scenarioId, activeProfileId, {
    enabled:
      activeMode === Co2MaterialSettingType.Global ||
      (activeMode === Co2MaterialSettingType.Project && !!props.projectId) ||
      (activeMode === Co2MaterialSettingType.Scenario && !!props.scenarioId) ||
      (activeMode === Co2MaterialSettingType.Profile && !!activeProfileId),
  });

  const upsertCo2MaterialSettings = useUpsertCo2MaterialSettings();

  useEffect(() => {
    if (!appSettings.data?.profileLevelRebarSettings) return;

    if (profileData && profileData.length > 0) {
      const filteredProfiles = profileData.filter((profile) => profile.rebarEnabled);
      const _profileOptions = filteredProfiles.map((profile) => ({
        label: profile.name ?? "",
        value: profile.profileId ?? "",
      }));

      setProfileOptions([{ label: i18n.t("cad-model-category-filter.profile.select-profile"), value: "" }, ..._profileOptions]);

      if (props.mode === Co2MaterialSettingType.Scenario && props.profileId) {
        setSelectedProfileValue(props.profileId);
        setActiveProfileId(props.profileId);
        setActiveMode(Co2MaterialSettingType.Scenario);
      } else if (props.originScreen === "Admin") {
        const firstProfileId = _profileOptions[0]?.value ?? "";
        setSelectedProfileValue(firstProfileId);
        setActiveProfileId(firstProfileId);
        setActiveMode(Co2MaterialSettingType.Profile);
      } else if (props.profileId) {
        setSelectedProfileValue(props.profileId);
        setActiveProfileId(props.profileId);
        setActiveMode(Co2MaterialSettingType.Profile);
      }
    } else {
      setProfileOptions([{ label: i18n.t("cad-model-category-filter.profile.select-profile"), value: "" }]);
      setSelectedProfileValue("");
      setActiveProfileId(null);
    }
  }, [profileData]);

  useEffect(() => {
    if (co2MaterialAssignSettings?.data?.status !== undefined) {
      setIsChecked(co2MaterialAssignSettings.data.status);
    }
  }, [co2MaterialAssignSettings.data]);

  const handleProfileChange = (event: SelectChangeEvent) => {
    const newProfileId = event.target.value;
    setSelectedProfileValue(newProfileId);

    if (newProfileId) {
      setActiveProfileId(newProfileId);
      setActiveMode(Co2MaterialSettingType.Profile);
    }
  };

  const handleGlobalRebarChange = async (changeValue: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(changeValue.target.checked);
    const enableRebar = changeValue.target.checked;
    const co2MaterialAssignSettingId = co2MaterialAssignSettings?.data?.co2MaterialAssignSettingId ?? null;
    const upsertCo2MaterialSettingRequest: UpsertCo2MaterialSettingRequest = createPayload(co2MaterialAssignSettingId, enableRebar);
    await upsertCo2MaterialSettings.mutateAsync(upsertCo2MaterialSettingRequest);
    await co2MaterialAssignSettings.refetch();
  };

  const createPayload = (co2MaterialAssignSettingId: string | null, enableRebar: boolean): UpsertCo2MaterialSettingRequest => {
    const modeKey = activeMode.toLowerCase();
    return new UpsertCo2MaterialSettingRequest({
      co2MaterialAssignSettingId,
      co2MaterialAssignSettingType: activeMode,
      description: i18n.t("rebar-estimation-tab.default-settings-description", {
        mode: `$t(rebar-estimation-tab.mode.${modeKey})`,
      }),
      name: i18n.t("rebar-estimation-tab.default-settings-name", { mode: `$t(rebar-estimation-tab.mode.${modeKey})` }),
      enableRebar,
      projectId: props.projectId,
      scenarioId: props.scenarioId,
      profileId: activeProfileId,
    } as UpsertCo2MaterialSettingRequest);
  };

  if (!co2MaterialAssignSettings.data) return null;

  const confirmSaveProfile = async () => {
    await handleSaveProfile();
  };

  const handleSaveProfile = async () => {
    if (props.profileId === selectedProfileValue) return;

    setIsSubmitting(true);
    setErrorMessage("");
    setShowErrorMessage(false);

    try {
      const updateRequest = new UpdateScenarioRequest({
        scenarioId: props.scenarioId ?? "",
        profileId: selectedProfileValue ?? undefined,
      });

      await updateScenario.mutateAsync(updateRequest as any);

      setSnackbarMessage(i18n.t("rebar-estimation-tab.profile-update-success"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setShowConfirmDialog(false);
    } catch (error) {
      console.error("Error updating profile:", error);

      setSnackbarMessage(i18n.t("rebar-estimation-tab.profile-update-failure"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("rebar-estimation-tab.rebar-estimation")}
          </Typography>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Card elevation={3} sx={{ width: "100%" }}>
            <CardContent>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid>
                  <Box>
                    {appSettings.data?.profileLevelRebarSettings === true ? (
                      props.originScreen == "Project" ? (
                        <Typography variant="body1">{i18n.t("rebar-estimation-tab.configured-profile")}</Typography>
                      ) : (
                        <Typography variant="body1">{i18n.t("rebar-estimation-tab.configure-profile")}</Typography>
                      )
                    ) : (
                      <>
                        <Typography variant="body1">{i18n.t("rebar-estimation-tab.enable-rebar-estimation-toggle")}</Typography>
                        <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                          {i18n.t("rebar-estimation-tab.enable-rebar-message")}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  {appSettings.data?.profileLevelRebarSettings && props.rebarEnabled !== RebarEstimationStatus.Disabled && (
                    <Select disabled={props.originScreen == "Project"} value={selectedProfileValue} onChange={handleProfileChange} displayEmpty sx={{ width: "250px" }}>
                      {profileOptions.map((profile) => (
                        <MenuItem key={profile.value} value={profile.value}>
                          {profile.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Grid>

                <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                  {!appSettings.data?.profileLevelRebarSettings && <Switch checked={isChecked} onChange={handleGlobalRebarChange} />}
                </Grid>

                {appSettings.data?.profileLevelRebarSettings && props.originScreen === "Scenario" && (
                  <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={props.profileId === selectedProfileValue || selectedProfileValue === "" || isSubmitting}
                    >
                      {i18n.t("rebar-estimation-tab.save-profile")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 1, width: "100%", position: "relative" }}>
          {appSettings.data?.profileLevelRebarSettings && props.rebarEnabled === RebarEstimationStatus.Disabled ? (
            <span>{i18n.t("rebar-estimation-tab.disabled-setting-message")}</span>
          ) : appSettings.data?.profileLevelRebarSettings && isChecked && props.originScreen !== "Admin" ? (
            <RebarEstimationGrid mode={activeMode} projectId={props.projectId} scenarioId={props.scenarioId} profileId={activeProfileId} originScreen={props.originScreen} />
          ) : appSettings.data?.profileLevelRebarSettings && !isChecked && props.originScreen !== "Admin" ? (
            <span>{i18n.t("rebar-estimation-tab.disabled-setting-message")}</span>
          ) : (
            <RebarEstimationGrid mode={activeMode} projectId={props.projectId} scenarioId={props.scenarioId} profileId={activeProfileId} originScreen={props.originScreen} />
          )}
        </Grid>
      </Grid>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>{i18n.t("rebar-estimation-tab.override-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("rebar-estimation-tab.override-confirmation", {
              oldProfile: profileOptions.find((p) => p.value === props.profileId)?.label ?? "",
              newProfile: profileOptions.find((p) => p.value === selectedProfileValue)?.label ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowConfirmDialog(false)}>
            {i18n.t("rebar-estimation-tab.cancel")}
          </Button>
          <Button autoFocus onClick={confirmSaveProfile} variant="contained" type="submit">
            {i18n.t("rebar-estimation-tab.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

export default RebarEstimationPage;
